/**
 * The blocks picker, with the price computed as you change it.
 *
 * `.docs/20-studio-portal-pages.md` §DRY watchlist: "`PackageBuilder` and
 * `QuoteBuilder` are the only price-composition UIs; both call
 * `catalog_compute_price`." Two screens compose a package — the onboarding wizard's
 * fourth step (§B) and "Edit retainer" on the client page (§C) — and the second one
 * is the first one plus a retainer to diff against, which is what `current` is for.
 *
 * **Nothing here computes a price.** Every figure comes from `catalog_compute_price`,
 * which is the same function `retainers_start$` and `retainers_change_package$` price
 * with, so the number on the confirm button and the number on the invoice are the
 * same number. `PRICING.md`'s bundle bands, the market tier, the conversion and the
 * rounding are all steps in the answer and are printed as `Breakdown` renders them —
 * a builder that added up block prices in the browser would disagree with the engine
 * the first time somebody crossed a discount band.
 *
 * The debounce is not cosmetic. Every keystroke on a block count is a `POST`, and the
 * answers can arrive out of order; `useApiQuery` keys on the input, so the *stale*
 * answer for a package that is no longer on screen cannot be rendered — but firing
 * one per keystroke would still be forty calls to type a package.
 */

import { useEffect, useMemo, useRef } from 'react'

import type {
  ComputedPrice,
  Currency,
  CustomLineInput,
  PackageItemInput,
  TierCode,
} from '@fareast/shared'

import { useStudioRead, type StudioCallInput } from '../../api/studio.js'
import { CustomLineList } from '../client/customLines.js'
import { useFormat } from '../../format/useFormat.js'
import { Button, ButtonRow } from '../portal/Button.js'
import { Empty } from '../portal/Disclosure.js'
import { Row, ScrollX, Stack } from '../portal/Layout.js'
import { Notice } from '../portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { Muted, Num, Small } from '../portal/Text.js'
import { Breakdown } from './Breakdown.js'
import { useDebounced } from './useDebounced.js'

/** A service a package may hold, as `catalog_list_services` answers it. */
export type PackageService = {
  readonly code: string
  readonly name: string
  readonly category: string
  readonly block_scope: string
  readonly quote_only: boolean
  readonly active: boolean
  readonly replaces_service: string | null
}

export type PackageBuilderProps = {
  /** Every service on the card. The builder filters to the monthly, active ones. */
  readonly services: readonly PackageService[]
  readonly tier: TierCode
  readonly currency: Currency
  /** The package as it stands. `[]` for a client with nothing yet. */
  readonly value: readonly PackageItemInput[]
  readonly onChange: (items: readonly PackageItemInput[]) => void
  /**
   * The retainer's current items, when there is one.
   *
   * Given, the builder prices a `package_change` and the answer carries the before,
   * the after and the monthly delta — which is what "Edit retainer" has to show and
   * what onboarding has nothing to show.
   */
  readonly current?: readonly PackageItemInput[] | undefined
  /**
   * The custom lines the package carries, read-only.
   *
   * `PRICING.md` §Custom lines: a fixed amount agreed with one client, charged as
   * given. They are **not composable here** — this builder is the rate card and a
   * custom line is by definition not on it — but they are part of the price, so they
   * go into the request and they are listed under the blocks. A builder that priced
   * only what it could edit would put a total on the confirm button that the invoice
   * disagrees with.
   *
   * Empty for onboarding, which creates none.
   */
  readonly customLines?: readonly CustomLineInput[] | undefined
  /** Handed the price whenever it changes, so a page can put it on a confirm button. */
  readonly onPriced?: ((price: ComputedPrice | null) => void) | undefined
}

/**
 * What `catalog_compute_price` takes, read off the action itself.
 *
 * `as const` was doing this job and doing it wrongly: it made every array `readonly`,
 * which the action's own input does not accept — and nothing said so, because
 * `defineAction` erased its input type and every studio body was `{}` until session
 * 20's review. Naming the type is both the fix and the check.
 */
type PriceRequest = StudioCallInput<'catalog_compute_price'>['request']

/**
 * One frozen empty array, not a `[]` in the parameter list.
 *
 * The default value is a dependency of the `useMemo` that builds the request, and a
 * fresh literal on every render is a fresh request object on every render — which is
 * a new query key, which is a `POST` per keystroke with no debounce in front of it.
 */
const EMPTY_CUSTOM_LINES: readonly CustomLineInput[] = []

export function PackageBuilder({
  services,
  tier,
  currency,
  value,
  onChange,
  current,
  customLines = EMPTY_CUSTOM_LINES,
  onPriced,
}: PackageBuilderProps) {
  const format = useFormat()
  const items = useDebounced(value)

  const choosable = useMemo(
    () => services.filter((one) => one.category === 'monthly' && one.active && !one.quote_only),
    [services],
  )

  /*
   * A package with no blocks is not priced at all.
   *
   * `PRICING.md`'s minimum retainer is one block **or one custom line**, and this
   * builder composes blocks — so "nothing to price" is still no blocks, and
   * `enabled: false` is the honest version of it. A package that is custom lines and
   * nothing else is not something this screen can compose; it is proposed by the
   * lines themselves and priced with them.
   *
   * The custom lines ride in both halves of a `package_change` unchanged: this
   * builder cannot edit one, so they are the same before and after, and leaving them
   * out would answer a delta for a package that dropped them.
   */
  const request = useMemo<PriceRequest>(
    () =>
      current === undefined
        ? { kind: 'monthly', items: [...items], custom_lines: [...customLines] }
        : {
            kind: 'package_change',
            current: [...current],
            current_custom_lines: [...customLines],
            proposed: [...items],
            proposed_custom_lines: [...customLines],
          },
    [current, customLines, items],
  )

  const price = useStudioRead(
    'catalog_compute_price',
    { request, tier, currency },
    { enabled: items.length > 0, inline: true },
  )

  const computed = price.data ?? null

  /*
   * The callback lives in a ref, and the effect depends on the **price** alone.
   *
   * A caller writing `onPriced={(p) => { setPrice(p) }}` hands a new function on every
   * render; with `onPriced` in the dependency list the effect re-runs, the caller sets
   * state, and the two loop until React gives up with "maximum update depth exceeded"
   * — several frames away from anything about pricing. The ref is what makes the
   * ordinary call site safe without asking every caller to `useCallback`.
   */
  const notify = useRef(onPriced)
  notify.current = onPriced
  useEffect(() => {
    notify.current?.(computed)
  }, [computed])

  const chosen = new Map(value.map((one) => [one.service, one.blocks]))

  const set = (code: string, blocks: number) => {
    if (blocks <= 0) {
      onChange(value.filter((one) => one.service !== code))
      return
    }
    onChange(
      chosen.has(code)
        ? value.map((one) => (one.service === code ? { ...one, blocks } : one))
        : [...value, { service: code, blocks }],
    )
  }

  return (
    <Stack>
      <ScrollX>
        <Table>
          <THead>
            <Tr>
              <Th>Block</Th>
              <Th>What one is</Th>
              <Th right>Blocks</Th>
            </Tr>
          </THead>
          <TBody>
            {choosable.map((service) => {
              const blocks = chosen.get(service.code) ?? 0
              return (
                <Tr key={service.code} variant={blocks > 0 ? undefined : 'sub'}>
                  <Td>{service.name}</Td>
                  <Td>
                    <Small>
                      <Muted>{service.block_scope}</Muted>
                    </Small>
                  </Td>
                  <Td right>
                    <Row>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={blocks === 0}
                        onClick={() => {
                          set(service.code, blocks - 1)
                        }}
                      >
                        <span aria-label={`One fewer ${service.name} block`}>−</span>
                      </Button>
                      <Num value={blocks} />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          set(service.code, blocks + 1)
                        }}
                      >
                        <span aria-label={`One more ${service.name} block`}>+</span>
                      </Button>
                    </Row>
                  </Td>
                </Tr>
              )
            })}
          </TBody>
        </Table>
      </ScrollX>

      {/*
        The lines this builder cannot compose, under the ones it can.
        They are in the price above and on the confirm button beyond it, so a reader
        who could not see them would be asked to approve a figure with an unexplained
        difference in it. `CustomLineList` renders nothing when there are none.
      */}
      <CustomLineList lines={customLines} />

      {value.length === 0 ? (
        <Empty>Choose at least one block. The minimum retainer is one.</Empty>
      ) : price.isError ? (
        <Notice need role="alert">
          We could not price that package. The blocks are still here; try again.
        </Notice>
      ) : computed === null ? (
        <p className="empty">Pricing…</p>
      ) : (
        <Stack gap="tight">
          <Breakdown price={computed} />
          {computed.change === null ? null : (
            <ButtonRow>
              <Small>
                <Muted>
                  {format.money(computed.change.current.total)} today,{' '}
                  {format.money(computed.change.proposed.total)} after —{' '}
                  {format.money(computed.change.delta)} a month.
                </Muted>
              </Small>
            </ButtonRow>
          )}
        </Stack>
      )}
    </Stack>
  )
}
